/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

import "./suppress-console";
import { LitElement, html, TemplateResult, render } from "lit";
import { customElement, property } from "lit/decorators.js";
//Microsoft-library for azure AD
import { PublicClientApplication, Configuration } from "@azure/msal-browser";

import "@material/mwc-top-app-bar-fixed";
import "@material/mwc-textfield";
import "@material/mwc-button";
import "@material/mwc-checkbox";
import "@material/mwc-formfield";
import "@material/mwc-dialog";

import "./queues";
import "./batch";
import "./review-document";
import { FileReviewRequest, PubSubReviewRequest, FirestoreReviewRequest, ReviewRequest } from "./review-request";
import { QueueType, Queue } from "./queues";
import { fetchRetry } from "./fetch-retry";
import { Checkbox } from "@material/mwc-checkbox";
import { Dialog } from "@material/mwc-dialog";

// Import logging functions
import { logAction, setPubsubAccessToken, configurePubsubLogger } from "./logger";

// Import save queue for tab close protection
import { globalSaveQueue } from "./save-queue";

// Import version information
import { APP_BUILD_NUMBER } from "../version";

// Import session manager for persistence
import { SessionManager, SessionData } from "./session-manager";

// to prevent an access token expiration, how many minutes before the expiration should the accessToken be renewed?
const MINUTES_BEFORE_ACCESS_TOKEN_RENEWAL = 20;


enum ComponentType {
  ManageBatchAndQueues,
  ReviewDocument,
}

export type IdentityType = "none" | "gis" | "ad";

@customElement("hitl-app")
export class HitlApp extends LitElement {
  @property({ type: Array })
  reviewRequests: ReviewRequest[] = [];

  @property({ type: Array })
  componentType: ComponentType = ComponentType.ManageBatchAndQueues;

  @property({ type: String })
  userId: string = "";

  @property({ type: String, attribute: 'queuetype' })
  queueType: QueueType = "File";

  @property({ type: String, attribute: 'identitytype' })
  identityType: IdentityType = "none";

  @property({ type: Array })
  queues: Queue[] = [];

  @property({ type: Boolean })
  loginRequired: boolean = false;

  @property({ type: Boolean })
  setupCompleted: boolean = false;

  @property({ type: Boolean })
  loadingReviewRequests: boolean = false;

  // Cached DOM references for performance
  private cachedElements = {
    dialog: null as HTMLElement | null,
    queues: null as HTMLElement | null,
    lastCacheTime: 0
  };
  private static readonly CACHE_DURATION = 3000; // 3 seconds

  @property({ type: Object })
  loadingProgress: { current: number; total: number; queueName?: string } | null = null;

  clientLogo?: string = process.env.CLIENT_LOGO || 'deloitte_logo.jpg';
  appTitle?: string = process.env.APP_TITLE || 'Human-in-the-Loop Benutzeroberfläche';

  @property({ type: String })
  detectedEnvironment: string = "";

  initTokenClient?: (
    config: google.accounts.oauth2.TokenClientConfig
  ) => google.accounts.oauth2.TokenClient;

  ADclientApplication?: PublicClientApplication;

  private tokenRefreshTimer?: number;
  private boundTaskCompletedHandler?: (event: CustomEvent) => void;
  private boundBeforeUnloadHandler?: (event: BeforeUnloadEvent) => void;

  override createRenderRoot() {
    return this;
  }

  #accessTokenAD: string = "";
  #accessToken: string = "";
  #idToken: string = "";
  #accessTokenExpiration: Date = new Date();
  #loggedUserName: string = "";
  #loggedUserEmail: string = "";
  #loggedUserAccessType: string = "";
  #sessionCreatedAt: string = "";

  override connectedCallback() {
    super.connectedCallback();
    // Attempt to restore session before initializing
    this.attemptSessionRestore();
  }

  override firstUpdated() {
    // load AI UI component scripts into document head
    let aiuiScriptElement = document.createElement("script");
    aiuiScriptElement.src =
      "https://aiui.googleapis.com/v1/component/client.js?key=" + process.env.API_KEY;
    if (process.env.AI_UI_API_VERSION) {
      aiuiScriptElement.src += "&version=" + process.env.AI_UI_API_VERSION
    };
    document.head.appendChild(aiuiScriptElement);

    // load Google Identity Services library and register callbacks to ensure that GIS is loaded before login button is used
    let gsiScriptElement = document.createElement("script");
    gsiScriptElement.src = "https://accounts.google.com/gsi/client";
    gsiScriptElement.onload = this.setupIdentityServices.bind(this);
    document.head.appendChild(gsiScriptElement);
    if (this.identityType === "gis") {
      this.loginRequired = true;
    }
    else if (this.identityType === "ad") {
      const configuration: Configuration = {
        auth: {
          clientId: process.env.AD_CLIENT_ID,
          authority: "https://login.microsoftonline.com/" + process.env.AD_TENANT_ID,
          redirectUri: process.env.AD_REDIRECT_URI
        }
      };
      this.ADclientApplication = new PublicClientApplication(configuration);
      this.ADclientApplication.initialize();
      this.loginRequired = true;
      this.setupCompleted = true;
    } else {
      this.loginRequired = false;
      this.setupCompleted = true;
    }

    // Listen for global task completion events
    this.boundTaskCompletedHandler = this.handleTaskCompleted.bind(this);
    document.addEventListener('task-completed', this.boundTaskCompletedHandler);

    // Set up tab close protection for pending saves
    this.boundBeforeUnloadHandler = this.handleBeforeUnload.bind(this);
    window.addEventListener('beforeunload', this.boundBeforeUnloadHandler);
  }

  private handleBeforeUnload(event: BeforeUnloadEvent): void {
    // Check if there are pending saves
    if (globalSaveQueue.hasPendingOperations()) {
      const status = globalSaveQueue.getStatus();
      const message = `Sie haben ${status.pending + status.inProgress} ausstehende Speichervorgänge. Möchten Sie wirklich fortfahren?`;

      // Modern browsers ignore custom messages, but setting returnValue triggers the warning
      event.preventDefault();
      event.returnValue = message;

      console.warn('User attempted to close tab with pending save operations:', status);

      return message;
    }
  }

  override render() {
    const template: TemplateResult[] = [];
    template.push(html` <mwc-dialog id="dialog"> </mwc-dialog> `);
    if (this.componentType === ComponentType.ManageBatchAndQueues) {
      template.push(html`
      <style>
        .mdc-top-app-bar-fixed {
          background-color: #008746;
        }

        .header-content {
          display: flex;
          align-items: center;
          justify-content: flex-start; /* Ensures the content is aligned to the left */
          width: 100%;
          padding: 10px;
        }

        .logo {
          height: 40px;
          margin-right: 10px;
          background-color: white; /* Adds white background to the logo */
          padding: 5px; /* Adjust padding as needed */
          border-radius: 5px; /* Optional: adds rounded corners */
        }

        .header-title {
          color: white;
          font-size: 20px;
          font-weight: bold;
        }

        .logout-button {
          --mdc-icon-button-size: 48px;
          --mdc-icon-size: 24px;
          color: white;
        }

        .user-info {
          color: white;
          font-size: 14px;
          margin-right: 16px;
          display: flex;
          align-items: center;
        }

        .content-wrapper {
          padding: 20px;
        }

        .rows {
          display: flex;
          justify-content: center;
          align-items: center;
          flex-direction: column;
          gap: 1rem;
        }

        .version-badge {
          position: fixed;
          bottom: 10px;
          right: 10px;
          background-color: rgba(0, 135, 70, 0.8);
          color: white;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 11px;
          font-family: monospace;
          z-index: 1000;
          cursor: help;
        }
      </style>
      <mwc-top-app-bar-fixed id="header" ?centertitle=${false}>
        <div slot="title" class="header-content">
          <img src="${this.clientLogo}" alt="Logo" class="logo">
          <span class="header-title">${this.appTitle}</span>
        </div>
        ${this.userId
          ? html`
              <div slot="actionItems" style="display: flex; align-items: center;">
                <span class="user-info">${this.#loggedUserName || this.#loggedUserEmail}</span>
                <mwc-icon-button
                  class="logout-button"
                  icon="logout"
                  title="Logout"
                  @click=${this.logout}
                ></mwc-icon-button>
              </div>
            `
          : ''}
      </mwc-top-app-bar-fixed>
        <main>
          <div class="content-wrapper">

            ${!this.userId
              ? this.loginRequired // login required and no userId available
                ? html`
                    <div class="rows">
                    <mwc-button
                      raised
                      style="margin: auto; min-width: 25ex;"
                      @click=${this.login}
                      label="Login"
                      ?disabled=${!this.setupCompleted}
                    ></mwc-button>
                    ${(process.env.NODE_ENV !== 'production') ||
                      window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1' ||
                      (this.detectedEnvironment && (this.detectedEnvironment.startsWith(process.env.CLIENT_PREFIX + '-dev-') || this.detectedEnvironment.startsWith(process.env.CLIENT_PREFIX + '-ci-') || this.detectedEnvironment.startsWith(process.env.CLIENT_PREFIX + '-poc-')))
                      ? html`
                        <mwc-formfield label="Are you using a Deloitte account?">
                          <mwc-checkbox id="deloitteCb" type="checkbox" @change=${this.updateIdentityType}></mwc-checkbox>
                        </mwc-formfield>
                      `
                      : ''}
                    </div>
                  `
                : // no login required and no userId available
                  html` <mwc-textfield
                    style="margin: auto;max-width: 25ex;"
                    id="userId"
                    required
                    value=${this.userId}
                    @change=${this.updateUserId}
                    label="User ID"
                  ></mwc-textfield>`
              : // userId available
                html`
                <hitl-queues
                  .queues=${this.queues}
                  .queueType=${this.queueType}
                  @queues-updated=${this.updateQueues}
                  @review-requests-updated=${this.addReviewRequests}
                  @review-requests-loading=${this.handleReviewRequestsLoading}
                  @review-requests-progress=${this.handleReviewRequestsProgress}
                  @review-requests-error=${this.handleReviewRequestsError}
                  @review-requests-completed=${this.handleReviewRequestsCompleted}
                  @environment-detected=${this.handleEnvironmentDetected}
                  @get-access-token=${this.getAccessTokenEvent}
                ></hitl-queues>
                <hitl-batch
                @batch-review-triggered=${this.startBatchReview}
                @review-requests-updated=${this.replaceReviewRequests}
                @task-released=${this.handleTaskReleased}
                @get-access-token=${this.getAccessTokenEvent}
                .reviewRequests=${this.reviewRequests}>
              </hitl-batch>
              </div>
              `}
          </div>
        </main>
        <div class="version-badge" title="Build Number">${APP_BUILD_NUMBER}</div>
      `);
    } else if (this.componentType === ComponentType.ReviewDocument) {
      template.push(html`
        <style>
          .version-badge {
            position: fixed;
            bottom: 10px;
            right: 10px;
            background-color: rgba(0, 135, 70, 0.8);
            color: white;
            padding: 4px 10px;
            border-radius: 4px;
            font-size: 11px;
            font-family: monospace;
            z-index: 1000;
            cursor: help;
          }
        </style>
        <hitl-review-document
          @manage-batch-and-queues-triggered=${this.manageBatchAndQueues}
          .reviewRequests=${this.reviewRequests}
          .userId=${this.userId}
          @get-access-token=${this.getAccessTokenEvent}
        ></hitl-review-document>
        <div class="version-badge" title="Build Number">${APP_BUILD_NUMBER}</div>
      `);
    }
    return template;
  }

  setupIdentityServices() {
    this.setupCompleted = true;
    this.initTokenClient = google.accounts.oauth2.initTokenClient;
  }

  // returns promise for accessToken either by using an existing token or by requesting a new token
  getAccessTokenEvent = async (e: CustomEvent) => {
    const refresh = e.detail.refresh;
    let resolve = e.detail.resolve;
    let reject = e.detail.reject;
    try {
      const [accessToken, idToken] = await this.getAccessToken(refresh);
      resolve([accessToken, idToken]);
    } catch (error) {
      reject(error);
    }
  };

  getAccessToken = async (refresh = false): Promise<[string, string]> => {
    if (this.identityType === "none") {
      return ["", ""];
    } else if (
      (!refresh &&
        this.#accessToken &&
        this.#accessTokenExpiration > new Date()) ||
      (refresh &&
        this.#accessTokenExpiration >
          new Date(
            new Date().getTime() +
              MINUTES_BEFORE_ACCESS_TOKEN_RENEWAL * 60 * 1000
          ))
    ) {
      return [this.#accessToken, this.#idToken];
    } else if (refresh || !this.#accessToken || this.#accessTokenExpiration <= new Date()) {
      // Token needs refresh (either explicitly requested or expired)
      if (this.identityType === "gis") {
        try {
          // Settle this promise in the response callback for requestAccessToken()
          const promise = new Promise<google.accounts.oauth2.TokenResponse>(
            (resolve, reject) => {
              if (this.initTokenClient) {
                const tokenClient = this.initTokenClient({
                  client_id: process.env.GIS_CLIENT_ID!,
                  scope:
                    "https://www.googleapis.com/auth/cloud-platform https://www.googleapis.com/auth/datastore email profile",
                  prompt: "",
                  callback: (
                    tokenResponse: google.accounts.oauth2.TokenResponse
                  ) => {
                    if (tokenResponse.error !== undefined) {
                      console.log("tokenResponse has an error");
                      reject(tokenResponse.error_description);
                    }
                    resolve(tokenResponse);
                  },
                  error_callback: (
                    error: google.accounts.oauth2.ClientConfigError
                  ) => {
                    console.log("error_callback");
                    reject(error);
                  },
                });
                tokenClient.requestAccessToken();
              } else {
                reject("initTokenClient not defined");
              }
            }
          );
          const tokenResponse = await promise;
          this.#accessToken = tokenResponse.access_token;
          this.#accessTokenExpiration = new Date(
            new Date().getTime() + parseInt(tokenResponse.expires_in) * 1000
          );

          const audience = process.env.INTERNAL_API_URL
          const saEmail = `${process.env.CLIENT_PREFIX}-${process.env.ENVIRONMENT}-hitl-supervisor@${process.env.PROJECT_ID}.iam.gserviceaccount.com`
          const idTokenUrl = `https://iamcredentials.googleapis.com/v1/projects/-/serviceAccounts/${saEmail}:generateIdToken`
          const idTokenResponse = await fetch(idTokenUrl, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + this.#accessToken
              },
              body: JSON.stringify({
                audience: audience,
                includeEmail: "true"
              })
          });

          if (!idTokenResponse.ok) {
            const errorText = await idTokenResponse.text();
            console.error("Token exchange failed:", errorText);
            throw new Error(`HTTP ${idTokenResponse.status}: ${errorText}`);
          }

          const idTokenResponseJson = await idTokenResponse.json();
          this.#idToken = idTokenResponseJson.token;
          this.#loggedUserAccessType = "supervisor";

          // Configure the logger with environment-aware topic if environment is detected
          if (this.detectedEnvironment) {
            configurePubsubLogger(this.detectedEnvironment, this.#accessToken);
          } else {
            // Fallback to legacy configuration for backward compatibility
            setPubsubAccessToken(this.#accessToken);
          }

          // Save session after token refresh (only if user is logged in)
          if (this.userId) {
            this.saveCurrentSession();
          }
        } catch (error) {
          console.log("requesting access token failed - redirecting to login screen");
          this.handleAuthenticationFailure(false);
        }
      }
      else if (this.identityType === "ad" && this.ADclientApplication) {
        try {
          const loginResponse = await this.ADclientApplication.loginPopup({
            scopes: ["openid", "profile", "email"],
          });
          this.#loggedUserName = loginResponse.account.name
          this.#loggedUserEmail = loginResponse.account.username
          if (loginResponse.idToken) {
            this.#accessTokenAD = loginResponse.accessToken;

            const body = JSON.stringify({
                ad_id_token: loginResponse.idToken,
                ad_access_token: loginResponse.accessToken
            });
            const tokenResponse = await fetch(process.env.HITL_CREDS_ENDPOINT_URL, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json"
                },
                body: body
            });

            if (!tokenResponse.ok) {
              const errorText = await tokenResponse.text();
              console.error("Token exchange failed:", errorText);
              throw new Error(`HTTP ${tokenResponse.status}: ${errorText}`);
            }

            const tokenResponseJson = await tokenResponse.json();
            this.#idToken = tokenResponseJson.id_token;
            this.#accessToken = tokenResponseJson.access_token;
            this.#accessTokenExpiration = new Date(
            new Date().getTime() + parseInt(tokenResponseJson.expires_in) * 1000
            );
	          this.#loggedUserAccessType = tokenResponseJson.access_type;

            // Save session after token refresh (only if user is logged in)
            if (this.userId) {
              this.saveCurrentSession();
            }

            // Configure logger with environment-aware topic name
            if (this.detectedEnvironment) {
              configurePubsubLogger(this.detectedEnvironment, this.#accessToken);
            } else {
              // Fallback to legacy configuration for backward compatibility
              setPubsubAccessToken(this.#accessToken);
            }
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          console.log("Azure AD authentication failed:", errorMessage);

          // Show error dialog before redirecting
          const dialog = this.getCachedDialog() as Dialog;
          if (dialog) {
            const { heading, isPermissionDenied } = this.getLoginErrorContent(errorMessage);
            dialog.heading = heading;

            if (isPermissionDenied) {
              render(
                html`
                  <div style="margin-bottom: 16px;">
                    <p>Sie haben keine Berechtigung, auf die HITL-Anwendung zuzugreifen.</p>
                    <p style="margin-top: 12px;">Bitte wenden Sie sich an Ihren Administrator, um die erforderlichen EntraID-Gruppenberechtigungen zu erhalten.</p>
                  </div>
                  <mwc-button slot="primaryAction" dialogAction="ok">OK</mwc-button>
                `,
                dialog
              );
            } else {
              render(
                html`
                  <div style="margin-bottom: 16px;">
                    <p>Bei der Anmeldung ist ein Fehler aufgetreten.</p>
                    <p style="margin-top: 12px;">Bitte versuchen Sie es erneut oder wenden Sie sich an den Support.</p>
                  </div>
                  <mwc-button slot="primaryAction" dialogAction="ok">OK</mwc-button>
                `,
                dialog
              );
            }
            dialog.open = true;
          }

          this.handleAuthenticationFailure(false);
        }
      };
      // update accessToken in all extendAckIdWorkers of Review Requests
      this.updateWorkerTokens();
    } else {
      console.log("cannot fetch access token due to missing user action - redirecting to login screen");
      this.handleAuthenticationFailure(false);
    }
    return [this.#accessToken, this.#idToken] as const;
  };

  updateIdentityType(e: Event) {
    const hitlAppElement = document.getElementById('hitlApp');

    if ((e.target! as Checkbox).checked) {
        hitlAppElement.setAttribute('identityType', 'gis');
        this.identityType = "gis";
    } else {
        hitlAppElement.setAttribute('identityType', 'ad');
        this.identityType = "ad";
        // Initialize AD client application when switching to AD identity
        if (!this.ADclientApplication) {
          const configuration: Configuration = {
            auth: {
              clientId: process.env.AD_CLIENT_ID,
              authority: "https://login.microsoftonline.com/" + process.env.AD_TENANT_ID,
              redirectUri: process.env.AD_REDIRECT_URI
            }
          };
          this.ADclientApplication = new PublicClientApplication(configuration);
          this.ADclientApplication.initialize();
        }
    }
  }

  login = async () => {
    // Set session creation timestamp BEFORE getting tokens
    // This ensures the timestamp is always earlier than token expiration
    this.#sessionCreatedAt = new Date().toISOString();

    await this.getAccessToken(true);
    // fetch user info
    let userInfoUrl;
    let accessToken;
    let idToken;
    if (this.identityType === "gis") {
      userInfoUrl = "https://www.googleapis.com/oauth2/v1/userinfo";
      accessToken = this.#accessToken;
      const response = await fetchRetry(
        userInfoUrl,
        {
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        },
        5000,
        3
      );
      const json = await response.json();
      this.#loggedUserName = json.name;
      this.#loggedUserEmail = json.email;
      this.userId = json.email;
    }
    else if (this.identityType === "ad") {
      this.userId = this.#loggedUserEmail;
    };
    if (this.userId) {

      idToken = this.#idToken;
      const loginUrl = `${process.env.INTERNAL_API_URL}/documents/hitl_logins_${this.#loggedUserAccessType}`;
      const queryResponse = await fetch(`${loginUrl}/${this.#loggedUserEmail}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
      });

      const loginData = {
        timestamp: new Date().toISOString(),
        documentId: this.#loggedUserEmail,
        userName: this.#loggedUserName,
        identityType: this.identityType,
        accessType: this.#loggedUserAccessType
      };
      if (!queryResponse.ok) {
        // User is not logged in atm; add new login entry in Firestore
        await fetch(loginUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(loginData)
        });
      }
      else {
        const responseData = await queryResponse.json();
        const currentTimestamp = Date.now();
        const timestamp = responseData.timestamp ? new Date(responseData.timestamp).getTime() : null;

        if (!timestamp || isNaN(timestamp)) {
          console.error("The 'timestamp' field is either missing or invalid.");
          throw new Error("Invalid or missing 'timestamp' format.");
        }

        if (currentTimestamp > timestamp + 5 * 60 * 1000) {
          // User login expired; proceeding to updating login entry in Firestore
          await fetch(`${loginUrl}/${this.#loggedUserEmail}`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(loginData)
          });
        }
        else {
          // User is already logged in
          console.error('User is already logged in - redirecting to login screen');
          this.handleAuthenticationFailure(true);

          return;
        }
      }
      // Log successful login
      logAction({
        timestamp: new Date().toISOString(),
        severity: 'INFO',
        documentId: this.userId,
        action: 'User logged in',
        additionalInfo: {
          identityType: this.identityType,
          userEmail: this.#loggedUserEmail,
          accessType: this.#loggedUserAccessType
        }
      });

      // Save session after successful login
      this.saveCurrentSession();
    }
  };

  logout = async () => {
    // Show confirmation dialog
    const dialog = this.getCachedDialog() as Dialog;
    if (dialog) {
      dialog.heading = "Abmelden";
      render(
        html`
          <div style="margin-bottom: 16px;">
            Sind Sie sicher, dass Sie sich abmelden möchten?
          </div>
          <mwc-button
            slot="primaryAction"
            dialogAction="confirm"
            @click=${() => this.silentLogout()}>
            Ja
          </mwc-button>
          <mwc-button
            slot="secondaryAction"
            dialogAction="cancel">
            Nein
          </mwc-button>
        `,
        dialog
      );
      dialog.open = true;
    } else {
      // Fallback to silent logout if dialog not available
      await this.silentLogout();
    }
  };

  silentLogout = async () => {
    // Log silent logout action
    if (this.userId) {
      const loginUrl = `${process.env.INTERNAL_API_URL}/documents/hitl_logins_${this.#loggedUserAccessType}/${this.#loggedUserEmail}`;

      const deleteResponse = await fetch(loginUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.#idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!deleteResponse.ok) {
        console.error(`Could not clear login from Firestore`);
      } else {
        console.log(`Successfully logout`);
      }
      logAction({
        timestamp: new Date().toISOString(),
        severity: 'INFO',
        userId: this.userId,
        action: 'User logged out (silent - HITL only)',
        additionalInfo: {
          identityType: this.identityType,
          userEmail: this.#loggedUserEmail,
          accessType: this.#loggedUserAccessType
        }
      });
    }

    // Clear local session only - skip identity provider logout
    SessionManager.clearSession();

    // Clear all authentication state and return to login screen
    this.handleAuthenticationFailure(true);
  };

  fullLogout = async () => {
    // Log full logout action
    if (this.userId) {
      logAction({
        timestamp: new Date().toISOString(),
        severity: 'INFO',
        userId: this.userId,
        action: 'User logged out (full - including identity provider)',
        additionalInfo: {
          identityType: this.identityType,
          userEmail: this.#loggedUserEmail
        }
      });
    }

    SessionManager.clearSession();

    // Handle identity provider logout
    if (this.identityType === "ad" && this.ADclientApplication) {
      // Azure AD logout
      try {
        await this.ADclientApplication.logoutPopup({
          mainWindowRedirectUri: window.location.href
        });
      } catch (error) {
        console.warn('Azure AD logout failed, continuing with local logout:', error);
      }
    } else if (this.identityType === "gis" && this.#accessToken) {
      // Google Identity Services - revoke access token
      try {
        const revokeUrl = `https://oauth2.googleapis.com/revoke?token=${this.#accessToken}`;
        await fetch(revokeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        });
        console.log('Google access token revoked successfully');
      } catch (error) {
        console.warn('Google token revocation failed, continuing with local logout:', error);
      }
    }

    // Clear all authentication state and return to login screen
    this.handleAuthenticationFailure(true);
  };

  updateUserId(e: InputEvent) {
    this.userId = (e.target as HTMLTextAreaElement).value;
  }

  updateQueues(e: CustomEvent) {
    this.queues = e.detail;
  }

  async addReviewRequests(e: CustomEvent) {
    const newReviewRequests = e.detail;
    const [accessToken, idToken] = await this.getAccessToken();
    newReviewRequests.forEach((reviewRequest: ReviewRequest) => {
      if (reviewRequest instanceof PubSubReviewRequest) {
        // setup ExtendAckIdWorker to regularly extend the ackId and thus prevent someone else picking up the message
        reviewRequest.extendAckIdWorker = new Worker(
          new URL("extend-ackid-worker.ts", import.meta.url),
          { type: "module" }
        );
        reviewRequest.extendAckIdWorker.postMessage([
          reviewRequest.subscriptionName,
          reviewRequest.ackId,
          accessToken,
        ]);
        reviewRequest.extendAckIdWorker.onmessage =
          this.ackIdExtensionError.bind(this);
      } else if (reviewRequest instanceof FirestoreReviewRequest) {
        // Firestore tasks don't need deadline extension like PubSub
        // Tasks are assigned with expiresAt and auto-released by cleanup processes
        console.log(`Firestore task ${reviewRequest.taskId} assigned, no deadline extension needed`);
      }
    });
    // Filter out duplicates before adding new requests
    const existingIds = new Set(this.reviewRequests.map(req => req.id()));
    const uniqueNewRequests = newReviewRequests.filter(req => !existingIds.has(req.id()));

    this.reviewRequests = this.reviewRequests.concat(uniqueNewRequests);

    if (uniqueNewRequests.length !== newReviewRequests.length) {
      console.log(`Filtered out ${newReviewRequests.length - uniqueNewRequests.length} duplicate requests`);
    }

    // Start periodic token refresh for worker tokens if we have review requests
    if (this.reviewRequests.length > 0) {
      this.startTokenRefreshTimer();
    }

    // Log addition of review requests
    logAction({
      timestamp: new Date().toISOString(),
      severity: 'INFO',
      userId: this.userId,
      action: 'Added review requests',
      additionalInfo: { count: newReviewRequests.length }
    });

    // Save updated session with new review requests
    this.saveCurrentSession();
  }

  async replaceReviewRequests(e: CustomEvent) {
    this.reviewRequests = e.detail;
    // Save session after replacing review requests
    this.saveCurrentSession();
  }

  handleReviewRequestsLoading = (e: CustomEvent) => {
    this.loadingReviewRequests = true;
    this.loadingProgress = { current: 0, total: e.detail.queuesCount };
    console.log(`Starting to load review requests from ${e.detail.queuesCount} queues`);
  }

  handleReviewRequestsProgress = (e: CustomEvent) => {
    this.loadingProgress = {
      current: e.detail.current,
      total: e.detail.total,
      queueName: e.detail.queueName
    };
    console.log(`Loading progress: ${e.detail.current}/${e.detail.total} (${e.detail.queueName})`);
  }

  handleReviewRequestsError = (e: CustomEvent) => {
    this.loadingReviewRequests = false;
    this.loadingProgress = null;
    console.error('Error loading review requests:', e.detail.error);

    // Show error dialog to user
    const dialog = this.getCachedDialog() as Dialog;
    if (dialog) {
      dialog.heading = "Fehler beim Laden der Arbeitsstapel";
      render(
        html`Es ist ein Fehler beim Laden der Arbeitsstapel aufgetreten: ${e.detail.error}
          <mwc-button slot="primaryAction" dialogAction="ok">OK</mwc-button>`,
        dialog
      );
      dialog.open = true;
    }
  }

  handleReviewRequestsCompleted = (e: CustomEvent) => {
    this.loadingReviewRequests = false;
    this.loadingProgress = null;
    console.log(`Finished loading ${e.detail.count} review requests`);
  }

  handleEnvironmentDetected = (e: CustomEvent) => {
    this.detectedEnvironment = e.detail.environment;
    console.log(`Environment detected in main app: ${this.detectedEnvironment}`);

    // If we already have an access token, configure the logger immediately
    if (this.#accessToken && this.detectedEnvironment) {
      configurePubsubLogger(this.detectedEnvironment, this.#accessToken);
    }
  }

  handleTaskReleased = (e: CustomEvent) => {
    console.log(`Task released from batch: ${e.detail.taskId} from queue ${e.detail.queueName}`);

    // Find the queues component and handle task release properly
    const queuesComponent = this.getCachedQueuesComponent() as any;
    if (queuesComponent && typeof queuesComponent.handleTaskRelease === 'function') {
      console.log('🔄 Handling task release for queue count update');
      queuesComponent.handleTaskRelease(e.detail.queueName, 1);
    } else if (queuesComponent && typeof queuesComponent.refreshQueueCountsImmediate === 'function') {
      // Fallback to immediate refresh if handleTaskRelease is not available
      console.log('🔄 Triggering immediate queue refresh after task release');
      queuesComponent.refreshQueueCountsImmediate().catch((error: any) =>
        console.warn('Failed to refresh queue counts after task release:', error)
      );
    }
  }

  handleTaskCompleted = (e: CustomEvent) => {
    console.log(`Task completed: ${e.detail.taskId} from queue ${e.detail.queueName}`);

    // Find the queues component and trigger immediate refresh
    const queuesComponent = this.getCachedQueuesComponent() as any;
    if (queuesComponent && typeof queuesComponent.refreshQueueCountsImmediate === 'function') {
      console.log('🔄 Triggering immediate queue refresh after task completion');
      queuesComponent.refreshQueueCountsImmediate().catch((error: any) =>
        console.warn('Failed to refresh queue counts after task completion:', error)
      );
    }
  }


  ackIdExtensionError(e: MessageEvent) {
    console.log("ackIdExtensionError");
    const ackId = e.data;

    // Find the failed document
    const failedRequest = this.reviewRequests.find(
      (reviewRequest) => (reviewRequest as PubSubReviewRequest).ackId === ackId
    );

    // Remove the failed document from the batch
    const previousCount = this.reviewRequests.length;
    this.reviewRequests = this.reviewRequests.filter(
      (reviewRequest) => (reviewRequest as PubSubReviewRequest).ackId !== ackId
    );
    const remainingCount = this.reviewRequests.length;

    // Log the removal for debugging purposes but don't interrupt the user
    console.log(`Document with ackId ${ackId} silently removed from batch (${previousCount} -> ${remainingCount} documents)`);
    if (failedRequest) {
      console.log(`Removed document: ${failedRequest.name}`);
    }

    // Save session after removing failed request
    this.saveCurrentSession();

    // Only redirect to landing page if there are no more documents to review
    if (remainingCount === 0) {
      // cleanup leftover from DocumentView component
      document
        .querySelectorAll(".cdk-overlay-container")
        .forEach((element) => (element.innerHTML = ""));
      // change to manage batch and queue view
      this.componentType = ComponentType.ManageBatchAndQueues;
    } else {
      // If we're currently in review mode, notify the review component about the updated list
      if (this.componentType === ComponentType.ReviewDocument) {
        const reviewComponent = document.querySelector("hitl-review-document") as any;
        if (reviewComponent) {
          // Update the review component's list and adjust the index if necessary
          reviewComponent.reviewRequests = this.reviewRequests;
          // If the current index is beyond the new list length, adjust it
          if (reviewComponent.reviewRequestsIndex >= this.reviewRequests.length) {
            reviewComponent.reviewRequestsIndex = Math.max(0, this.reviewRequests.length - 1);
          }
          // Continue reviewing with the updated list
          reviewComponent.reviewDocument(this.reviewRequests[reviewComponent.reviewRequestsIndex]);
        }
      }
    }
  }

  startBatchReview(e: CustomEvent) {
    this.reviewRequests = e.detail;
    this.componentType = ComponentType.ReviewDocument;
  }

  manageBatchAndQueues(e: CustomEvent) {
    // cleanup leftover from DocumentView component
    document
      .querySelectorAll(".cdk-overlay-container")
      .forEach((element) => (element.innerHTML = ""));
    this.reviewRequests = e.detail;
    this.componentType = ComponentType.ManageBatchAndQueues;
  }

  // Cached DOM reference helpers for performance
  private getCachedDialog(): HTMLElement | null {
    const now = Date.now();
    if (!this.cachedElements.dialog || (now - this.cachedElements.lastCacheTime) > HitlApp.CACHE_DURATION) {
      this.cachedElements.dialog = document.getElementById("dialog");
      this.cachedElements.lastCacheTime = now;
    }
    return this.cachedElements.dialog;
  }

  private getCachedQueuesComponent(): HTMLElement | null {
    const now = Date.now();
    if (!this.cachedElements.queues || (now - this.cachedElements.lastCacheTime) > HitlApp.CACHE_DURATION) {
      this.cachedElements.queues = document.querySelector('hitl-queues');
      this.cachedElements.lastCacheTime = now;
    }
    return this.cachedElements.queues;
  }

  /**
   * Determines error type and dialog content for login failures
   * @param errorMessage The error message from the login attempt
   * @returns Object with heading and whether it's a permission error
   */
  getLoginErrorContent(errorMessage: string): {
    heading: string;
    isPermissionDenied: boolean;
  } {
    const isPermissionDenied = errorMessage.toLowerCase().includes("does not have permissions");
    return {
      heading: isPermissionDenied ? "Zugriff verweigert" : "Anmeldung fehlgeschlagen",
      isPermissionDenied
    };
  }

  // Update access tokens in all active ACK extension workers
  private updateWorkerTokens() {
    if (!this.#accessToken) return;

    console.log(`Updating access tokens in ${this.reviewRequests.length} workers`);
    this.reviewRequests.forEach((reviewRequest) => {
      if (reviewRequest instanceof PubSubReviewRequest && reviewRequest.extendAckIdWorker) {
        try {
          reviewRequest.extendAckIdWorker.postMessage({
            type: 'refresh_token',
            token: this.#accessToken,
            idToken: this.#idToken
          });
        } catch (error) {
          console.warn('Failed to update token in worker:', error);
        }
      }
    });
  }

  // Start periodic token refresh for worker tokens
  private startTokenRefreshTimer() {
    // Clear any existing timer
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
    }

    // Refresh worker tokens every 10 minutes (more aggressive to prevent expiration)
    this.tokenRefreshTimer = setInterval(async () => {
      try {
        console.log('Performing periodic token refresh for workers');
        // Force a refresh if token will expire in next 25 minutes
        const willExpireSoon = this.#accessTokenExpiration <= new Date(new Date().getTime() + 25 * 60 * 1000);
        if (willExpireSoon) {
          console.log('Token will expire soon, forcing refresh');
          await this.getAccessToken(true);
        } else {
          // Just update workers with current token
          this.updateWorkerTokens();
        }
      } catch (error) {
        console.error('Periodic token refresh failed - redirecting to login screen:', error);
        this.handleAuthenticationFailure(true);
      }
    }, 10 * 60 * 1000); // 10 minutes
  }

  private attemptSessionRestore() {

    const sessionData = SessionManager.loadSession();

    if (!sessionData) {
      console.log("[HitlApp] No session found in localStorage");
      return;
    }

    if (!SessionManager.isSessionValid(sessionData)) {
      console.log("[HitlApp] Session invalid or expired - clearing session");
      SessionManager.clearSession();
      return;
    }

    console.log("[HitlApp] Valid session found - restoring user state");

    // Restore authentication tokens
    this.#accessToken = sessionData.accessToken;
    this.#accessTokenAD = sessionData.accessTokenAD;
    this.#idToken = sessionData.idToken;
    this.#accessTokenExpiration = new Date(sessionData.accessTokenExpiration);

    // Restore user information
    this.userId = sessionData.userId;
    this.#loggedUserName = sessionData.userName;
    this.#loggedUserEmail = sessionData.userEmail;

    // Preserve the original session creation timestamp
    this.#sessionCreatedAt = sessionData.sessionCreatedAt;



    // Always allow login bypass if session is valid, regardless of identity type mismatch
    this.loginRequired = false;


    // Configure logger with restored token
    if (this.detectedEnvironment) {
      configurePubsubLogger(this.detectedEnvironment, this.#accessToken);
    } else {
      setPubsubAccessToken(this.#accessToken);
    }

    // Start token refresh timer
    this.startTokenRefreshTimer();

    console.log("[HitlApp] Session restored successfully ✓", {
      userId: this.userId,
      loginRequired: this.loginRequired,
      reviewRequestsCount: this.reviewRequests.length
    });
  }

  private saveCurrentSession() {
    if (!this.userId || this.identityType === 'none') {
      return;
    }


    const sessionData: SessionData = {
      accessToken: this.#accessToken,
      accessTokenAD: this.#accessTokenAD,
      idToken: this.#idToken,
      accessTokenExpiration: this.#accessTokenExpiration.toISOString(),
      userId: this.userId,
      userName: this.#loggedUserName,
      userEmail: this.#loggedUserEmail,
      identityType: this.identityType,
      sessionCreatedAt: this.#sessionCreatedAt || new Date().toISOString(),
    };

    SessionManager.saveSession(sessionData);
  }

  private handleAuthenticationFailure(clearTokens: boolean = true) {
    console.log("Clearing session and returning to login screen");

    // Clear persisted session
    SessionManager.clearSession();
    window.sessionStorage.clear();

    // Clear authentication state
    if (clearTokens) {
      this.#accessToken = "";
      this.#accessTokenAD = "";
      this.#idToken = "";
      this.#accessTokenExpiration = new Date();
    }
    this.userId = "";
    this.loginRequired = true;
    this.#loggedUserName = "";
    this.#loggedUserEmail = "";
    this.#sessionCreatedAt = "";

    // Clear review requests and any ongoing work
    this.reviewRequests.forEach((reviewRequest) => {
      if (reviewRequest instanceof PubSubReviewRequest && reviewRequest.extendAckIdWorker) {
        try {
          reviewRequest.extendAckIdWorker.terminate();
        } catch (error) {
          console.warn('Failed to terminate worker:', error);
        }
      }
    });
    this.reviewRequests = [];

    // Clear any timers
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
      this.tokenRefreshTimer = undefined;
    }

    // Return to the login screen view
    this.componentType = ComponentType.ManageBatchAndQueues;

    // Force a re-render to show the clean login screen
    this.requestUpdate();

    console.log("User session cleared - showing login screen");
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    // Clean up timer when component is destroyed
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
    }
    // Clean up event listeners
    if (this.boundTaskCompletedHandler) {
      document.removeEventListener('task-completed', this.boundTaskCompletedHandler);
    }
    if (this.boundBeforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.boundBeforeUnloadHandler);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "hitl-app": HitlApp;
  }
}
